import * as React from "react";

import { useId, useMergedRefs } from "@fluentui/react-components";

import type {
  AppFlyoutAnchorProps,
  AppFlyoutAnchorState,
} from "./AppFlyoutAnchor.types";
import { InputMethod as CommonInputMethod } from "@1js/acui-common";
import { AppContext } from "@1js/acui-context";
import { checkExperiment, ribbonExperiments } from "@1js/acui-experiments";
import type {
  AppSplitButtonProps,
  AppSplitButtonState,
} from "../../AppSplitButton/next";
import { shouldAutoOpenMenu } from "../../../utilities";
import { useAppFlyoutAnchorRestoreFocusFixEnabled } from "./useAppFlyoutAnchorRestoreFocusFixEnabled";
import { mergeCallbacks } from "@fluentui/react-components";

// Input methods for invoking the menu.
// This is used to set the focus correctly when the menu is opened.
export type InputMethod = "keyboard" | "mouse" | "programmatic";

/**
 * Takes a menu button state and makes sure that the control receives a 'checked' prop,
 * thus allowing it to be styled when its menu is expanded. It further makes sure that
 * no tooltips on the original button are shown while the menu is expanded.
 */
export const useControlledContextualMenuByMenuButton = (
  props: AppFlyoutAnchorProps | AppSplitButtonProps,
  state: AppFlyoutAnchorState | AppSplitButtonState,
  targetRef?: React.Ref<HTMLElement>
) => {
  const shouldRestoreFocus = React.useRef(false);
  const { onAfterMenuDismiss, id, location, onExecute, preventMenuExecution } =
    props;

  const menuButtonId = useId("", state.menuButton.id);
  state.menuButton.id = menuButtonId;

  // This tracks if the contextual menu should be shown and the input method
  // that caused it to be shown.
  const [showContextualMenu, setShowContextualMenu] = React.useState<
    false | InputMethod
  >(false);
  const firstRender = React.useRef(true);
  const context = React.useContext(AppContext);

  const isAppFlyoutAnchorRestoreFocusFixEnabled =
    useAppFlyoutAnchorRestoreFocusFixEnabled();

  const onMarkFocusRestoreForKeyboardDismiss = React.useCallback(
    (ev?: React.KeyboardEvent<HTMLElement>) => {
      if (
        isAppFlyoutAnchorRestoreFocusFixEnabled &&
        ev &&
        (ev.key === "Escape" ||
          (ev.altKey && ev.key === "ArrowUp") ||
          ev.key === "Alt")
      ) {
        shouldRestoreFocus.current = true;
      }
    },
    [isAppFlyoutAnchorRestoreFocusFixEnabled]
  );

  const onHideContextualMenu = React.useCallback(
    () => setShowContextualMenu(false),
    []
  );

  const onButtonClick = React.useCallback(
    (
      ev: React.MouseEvent<HTMLButtonElement> &
        React.MouseEvent<HTMLAnchorElement>
    ) => {
      ev.preventDefault(); // don't navigate
      if (!showContextualMenu) {
        onExecute &&
          onExecute({
            id,
            location,
            inputMethod: CommonInputMethod.Mouse,
            event: ev.nativeEvent,
          });

        // If preventMenuExecution, we want to ensure we prevent default and log
        // the event like we are doing above, but we should avoid opening the menu
        // because it's not ready yet
        if (preventMenuExecution) {
          return;
        }
        setShowContextualMenu("mouse");
      } else {
        setShowContextualMenu(false);
      }
    },
    [showContextualMenu, onExecute, id, location, preventMenuExecution]
  );

  const onButtonKeyDown = React.useCallback(
    (ev: React.KeyboardEvent<HTMLElement>) => {
      if (
        (ev.altKey && ev.key === "ArrowDown") ||
        ev.key === "Enter" ||
        ev.key === " "
      ) {
        ev.preventDefault();
        onExecute &&
          onExecute({
            id,
            location,
            inputMethod: CommonInputMethod.Keyboard,
            event: ev.nativeEvent,
          });
        setShowContextualMenu("keyboard");
      }
    },
    [onExecute, id, location]
  );

  // Hooks can't be used conditionally, making them outside of if-statement
  // and only using them in the else case
  const buttonRef = React.useRef<any>(null);
  const menuButtonRef = React.useRef<any>(state.menuButton.ref);
  const mergedButtonRef = useMergedRefs(menuButtonRef?.current, buttonRef);
  if (mergedButtonRef.current) {
    mergedButtonRef.current.dismissMenu = onHideContextualMenu;
  }

  /**
   * useLayoutEffect used here instead of useEffect due to a keytip bug. (ADO 6730459)
   * This aligns the execution exactly to how "componentDidUpdate" worked
   * in class components (synchronous execution). Which means our "onAfterMenuDismiss"
   * callback doesn't interfere with any subsequent actions after the menu was closed.
   * React reference: https://reactjs.org/docs/hooks-reference.html#uselayouteffect
   */
  React.useLayoutEffect(() => {
    if (firstRender.current) {
      // Prevent this effect from running on first render
      firstRender.current = false;
    } else if (!showContextualMenu && onAfterMenuDismiss) {
      if (isAppFlyoutAnchorRestoreFocusFixEnabled) {
        // V9 contextual menu onDismiss callbacks are interfering so we need to ensure
        // onAfterMenuDismiss is called after the menu is dismissed and then the focus
        // needs to be restored to the menu button.
        onAfterMenuDismiss(menuButtonId);
        const requestId = requestAnimationFrame(() => {
          if (document.hasFocus() && shouldRestoreFocus.current) {
            mergedButtonRef.current?.focus();
            shouldRestoreFocus.current = false;
          }
        });

        return () => {
          cancelAnimationFrame(requestId);
        };
      }
      onAfterMenuDismiss(menuButtonId);
    }
    return undefined;
  }, [
    isAppFlyoutAnchorRestoreFocusFixEnabled,
    showContextualMenu,
    onAfterMenuDismiss,
    menuButtonId,
    mergedButtonRef,
  ]);

  React.useLayoutEffect(() => {
    if (
      !showContextualMenu &&
      shouldAutoOpenMenu(props.menuDefinition, props.deferredMenuDefinition) &&
      (mergedButtonRef?.current?.clientWidth > 0 ||
        ((targetRef as React.RefObject<HTMLElement> | undefined)?.current
          ?.clientWidth ?? 0) > 0) // make sure that this button isn't hidden by scaling
    ) {
      setShowContextualMenu("programmatic");
    }
    return undefined;
  }, [
    showContextualMenu,
    mergedButtonRef,
    props.menuDefinition,
    props.deferredMenuDefinition,
    context,
    targetRef,
  ]);

  state.menuButton.onClick = onButtonClick;
  state.menuButton.onKeyDown = onButtonKeyDown;
  state.menuButton["aria-expanded"] = !!showContextualMenu;

  state.menu.hidden = !showContextualMenu;

  if (isAppFlyoutAnchorRestoreFocusFixEnabled) {
    state.menu.onDismiss = mergeCallbacks(
      onMarkFocusRestoreForKeyboardDismiss,
      onHideContextualMenu
    );
  } else {
    state.menu.onDismiss = onHideContextualMenu;
  }

  state.menu.onItemClick = onHideContextualMenu;

  const isShouldFocusOnContainerEnabled = !!checkExperiment(
    context,
    ribbonExperiments.shouldFocusOnContainerEnabled
  );

  state.menu.shouldFocusOnContainer =
    showContextualMenu === "mouse" &&
    (isShouldFocusOnContainerEnabled
      ? state.menuButton.shouldFocusOnContainer !== false
      : true);

  if (targetRef) {
    // If targetRef given, set as menu target
    state.menu.target = targetRef as React.RefObject<HTMLElement>;
  } else {
    // If not given, default to menuButton on state
    state.menuButton.ref = mergedButtonRef;
    state.menu.target = buttonRef;
  }
};
